import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { INewsCardArticle, NEWS_CARD_CATEGORY_COLOR } from '../../libs/models/conversations/ChatNewsCard';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import IconArrow from '../../assets/etc-icons/icon_card_news_arrow.svg';
import IconClose from '../../assets/etc-icons/icon_card_news_close.svg';
// 북마크/공유 기능 보류 - 추후 활성화
// import { ReactComponent as BookmarkIcon } from '../../assets/etc-icons/icon_card_news_bookmark.svg';
// import IconShare from '../../assets/etc-icons/icon_card_news_share.svg';
import IconInsight from '../../assets/etc-icons/icon_card_news_insight.svg';
import sampleArticleImage1 from '../../assets/images/news_sample_article_1.png';
import sampleArticleImage2 from '../../assets/images/news_sample_article_2.png';
import sampleArticleImage3 from '../../assets/images/news_sample_article_3.png';

interface ChatCardNewsProps {
  articles: INewsCardArticle[];
  initialIndex?: number;
  onIndexChange?: (index: number, article: INewsCardArticle) => void;
  onClose?: () => void;
  onBookmark?: (article: INewsCardArticle, isBookmarked: boolean) => void;
  onShare?: (article: INewsCardArticle) => void;
}

// IT Trend 아이템 클릭 시 노출되는 뉴스 카드 (예시 데이터 / 실제 데이터로 교체해서 사용)
export const SAMPLE_NEWS_ARTICLES: INewsCardArticle[] = [
  {
    id: 'sample-1',
    category: { label: '산업 / 도입사례', color: NEWS_CARD_CATEGORY_COLOR.industryAdoption },
    title: '투자 앱 추천, 리스트에서 루프로 바뀌다',
    description:
      '토스증권은 추천과 검색을 배치 리스트 중심에서 실시간 이벤트와 피드백을 흡수하는 데이터 루프로 확장하고 있어요.',
    source: { name: '토스 기술블로그', date: '2026.08.26' },
    imageUrl: sampleArticleImage1,
    bodyParagraphs: [
      '토스증권은 추천과 검색이 어떤 방향으로 진화하는지 소개하며, 배치와 사용자 클러스터 기반 추천에서 실시간 개인화로 이동하고 있다고 설명했어요. 예전에는 유저와 아이템 데이터를 모아 미리 추천 리스트를 계산해두는 방식이 현실적인 선택이었다고 해요.',
      '서비스가 성장하면서 사용자 관심사, 시장과 종목 이벤트, 콘텐츠 종류가 더 빠르게 변했어요. 이에 따라 추천은 정적인 리스트가 아니라 행동 이벤트와 아이템 변경 이벤트, 사용자 피드백을 계속 흡수하는 루프 형태의 시스템으로 바뀌고 있어요.',
      '검색도 키워드와 필터를 넘어 RAG(검색 증강 생성)와 그래프 RAG(graph RAG)로 확장되는 흐름이 제시됐어요. kt ds가 금융 고객의 개인화, 추천, 검색 고도화를 논의할 때 데이터 품질·피처 스토어·벡터 인덱스 운영까지 함께 봐야 한다는 점을 보여줘요.',
    ],
    reference: { label: '출처', text: '토스증권 추천과 검색은 어떻게 진화하고 있을까?' },
    insight: {
      label: '인사이트',
      content:
        '금융 고객의 개인화·추천·검색 고도화를 검토할 때는 모델 선택보다 데이터 품질, 행동 이벤트의 최신성, 피처 관리, 벡터 인덱스 운영, 설명 가능성을 함께 설계해야 해요. 배치형 추천을 실시간 루프로 전환하려면 갱신 주기와 피드백 검증 기준을 먼저 정하는 것이 중요해요.',
    },
  },
  {
    id: 'sample-2',
    category: { label: '빅테크 / 신기술', color: NEWS_CARD_CATEGORY_COLOR.bigTechTrend },
    title: '챗봇은 답하고, 워크는 일을 맡는다',
    description:
      '오픈AI가 챗GPT를 질문 응답 도구에서 여러 단계의 업무를 수행하는 ‘챗GPT 워크’ 방향으로 확장하고 있어요.',
    source: { name: 'AI 타임즈', date: '2026.08.26' },
    imageUrl: sampleArticleImage2,
    bodyParagraphs: [
      '오픈AI 제품 책임자 티볼트 소티오는 챗GPT의 다음 무게중심이 단순 대화가 아니라 실제 업무 수행으로 이동하고 있다고 설명했어요. 코딩 에이전트 코덱스에서 확인한 작업 방식을 일반 업무 영역으로 넓히려는 흐름입니다.',
      '챗GPT 워크는 목표 제시, AI의 계획 수립, 여러 단계 실행, 결과물 도출에 가까운 구조를 지향해요. 조사, 분석, 문서, 보고서, 프레젠테이션처럼 완성된 산출물이 필요한 일을 채팅 안에서 맡기는 방식입니다.',
      '이 변화는 사내 업무 AI도 ‘답변 품질’만으로 평가하기 어려워진다는 뜻이에요. 업무 목표를 어떻게 정의하고, 중간 산출물을 어떻게 검토하며, 권한과 데이터를 어디까지 줄지가 제품 경쟁력의 일부가 됩니다.',
    ],
    reference: {
      label: '출처',
      text: `[8월26일] 오픈AI가 '챗GPT'를 바꾸고 있다…티보가 밝힌 다음 단계는 ‘일하는 AI’ / AI타임스`,
    },
    insight: {
      label: '인사이트',
      content:
        '사내 업무 AI를 도입할 때는 답변 품질뿐 아니라 목표 정의, 단계별 결과 검토, 데이터 접근 권한, 승인과 공유 기준까지 운영 체계로 묶어야 해요. 반복되는 보고·조회·정리 업무부터 목표형 태스크로 설계하고, 사람이 개입할 지점을 명확히 두는 방식이 현실적이에요.',
    },
  },
  {
    id: 'sample-3',
    category: { label: '보안 / 리스크', color: NEWS_CARD_CATEGORY_COLOR.securityRisk },
    title: 'AI 에이전트에게 키를 보여주지 않는 법',
    description: '로컬 프로젝트의 .env와 셸 기록까지 읽는 AI 에이전트 환경에서 시크릿 노출을 줄이는 도구가 등장했어요.',
    source: { name: 'GeekNews', date: '2026.08.26' },
    imageUrl: sampleArticleImage3,
    bodyParagraphs: [
      'key-amnesia는 AI 에이전트가 프로젝트 파일과 명령 실행 환경에서 API 키나 자격 증명을 읽지 못하게 하려는 도구예요. 시크릿을 암호화된 볼트에 저장하고, 에이전트가 실행한 자식 프로세스의 환경변수에만 주입하는 방식입니다.',
      '기존의 .gitignore는 Git 커밋 유출을 막는 장치였지만, 에이전트가 로컬 파일과 MCP 설정, 셸 기록까지 접근하는 환경에서는 방어 범위가 부족해질 수 있어요. 명령 결과에 시크릿이 그대로 출력되면 반환 전에 검열하는 흐름도 포함됐습니다.',
      '다만 시크릿을 인코딩하거나 변형해 출력하는 경우에는 검열을 우회할 수 있고, 같은 OS 사용자 권한을 공유하는 프로세스 문제도 남아 있어요. 그래서 도구 하나보다 에이전트 권한, 승인 절차, 실행 격리를 함께 설계하는 쪽이 중요해졌습니다.',
    ],
    reference: {
      label: '출처',
      text: 'key-amnesia - AI 에이전트에 시크릿을 노출하지 않고 명령을 실행하는 도구 / GeekNews',
    },
    insight: {
      label: '인사이트',
      content:
        '개발 에이전트를 현장에 적용할 때는 모델 성능보다 로컬 실행 권한과 시크릿 노출 경로를 먼저 통제해야 해요. .env 접근 차단, 명시적 승인, 최소 권한, 실행 격리, 로그 마스킹을 공통 운영 기준으로 두고 우회 가능성을 정기적으로 점검해야 해요.',
    },
  },
];

export const ChatCardNews: React.FC<ChatCardNewsProps> = ({
  articles,
  initialIndex = 0,
  onIndexChange,
  onClose,
  // 북마크/공유 기능 보류 - 추후 활성화
  // onBookmark,
  // onShare,
}) => {
  const isDesktop = useAppSelector((state: RootState) => state.app.viewType === 'Desktop');
  const swiperRef = useRef<SwiperRef>(null);
  const isClosingRef = useRef(false);

  // 북마크/공유 기능 보류 - 추후 활성화
  // const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(
  //   () => new Set(articles.filter((item) => item.isBookmarked).map((item) => item.id)),
  // );

  useEffect(() => {
    swiperRef.current?.swiper.slideToLoop(initialIndex, 0);
  }, [initialIndex]);

  useEffect(() => {
    const swiper = swiperRef.current?.swiper;
    if (!swiper) return;
    swiper.allowTouchMove = !isDesktop;
    swiper.params.allowTouchMove = !isDesktop;
  }, [isDesktop]);

  // 팝업이 떠 있는 동안 뒤 배경 스크롤 막기
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const canNavigate = articles.length > 1;

  const handleClose = () => {
    isClosingRef.current = true;
    onClose?.();
  };

  // 북마크/공유 기능 보류 - 추후 활성화
  // const toggleBookmark = (item: INewsCardArticle) => {
  //   const next = !bookmarkedIds.has(item.id);
  //   setBookmarkedIds((prev) => {
  //     const updated = new Set(prev);
  //     if (next) {
  //       updated.add(item.id);
  //     } else {
  //       updated.delete(item.id);
  //     }
  //     return updated;
  //   });
  //   onBookmark?.(item, next);
  // };

  if (articles.length === 0) return null;

  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) {
    throw new Error('Modal root element not found');
  }

  const stopBackgroundInteraction = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  const renderNavButton = (direction: -1 | 1) => (
    <button
      type="button"
      className="news-card-nav-btn"
      aria-label={direction === -1 ? '이전 뉴스' : '다음 뉴스'}
      disabled={!canNavigate}
      onClick={() => {
        if (direction === -1) {
          swiperRef.current?.swiper.slidePrev();
        } else {
          swiperRef.current?.swiper.slideNext();
        }
      }}
    >
      <img
        src={IconArrow}
        alt=""
        draggable={false}
        className={classNames('news-card-nav-icon', { 'news-card-nav-icon--prev': direction === -1 })}
      />
    </button>
  );

  return createPortal(
    <div
      className="news-card-overlay"
      onClick={() => {
        handleClose();
      }}
      onDragStart={stopBackgroundInteraction}
      onDragEnter={stopBackgroundInteraction}
      onDragOver={stopBackgroundInteraction}
      onDragLeave={stopBackgroundInteraction}
      onDrop={stopBackgroundInteraction}
    >
      <div
        className="news-card-wrap"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {isDesktop && renderNavButton(-1)}

        <div className="news-card">
          <Swiper
            ref={swiperRef}
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
            loop={canNavigate}
            slidesPerView={1}
            spaceBetween={0}
            allowTouchMove={!isDesktop}
            threshold={10}
            initialSlide={initialIndex}
            onSlideChange={(swiper) => {
              if (isClosingRef.current || swiper.destroyed) return;
              const index = swiper.realIndex;
              onIndexChange?.(index, articles[index]);
            }}
          >
            {articles.map((item) => {
              return (
                <SwiperSlide key={item.id}>
                  <div className="news-card-slide">
                    <div className="news-card-actions swiper-no-swiping">
                      {/* 북마크/공유 기능 보류 - 추후 활성화
                      <div className="news-card-actions-left">
                        <button
                          type="button"
                          className={classNames('news-card-icon-btn', 'news-card-bookmark-btn', {
                            'is-bookmarked': isBookmarked,
                          })}
                          aria-label="북마크"
                          aria-pressed={isBookmarked}
                          onClick={() => {
                            toggleBookmark(item);
                          }}
                        >
                          <BookmarkIcon className="news-card-bookmark-icon" />
                        </button>
                        <button
                          type="button"
                          className="news-card-icon-btn"
                          aria-label="공유"
                          onClick={() => {
                            onShare?.(item);
                          }}
                        >
                          <img src={IconShare} alt="" draggable={false} />
                        </button>
                      </div>
                      */}
                      <div className="news-card-actions-left" />
                      <button
                        type="button"
                        className="news-card-icon-btn news-card-close"
                        aria-label="닫기"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClose();
                        }}
                      >
                        <img src={IconClose} alt="" draggable={false} />
                      </button>
                    </div>

                    <div className="news-card-meta">
                      <p className="news-card-category" style={{ color: item.category.color }}>
                        {item.category.label}
                      </p>
                      <h3 className="news-card-title">{item.title}</h3>
                      <p className="news-card-description">{item.description}</p>
                      <p className="news-card-source">
                        {item.source.name} {item.source.date}
                      </p>
                    </div>

                    <div className="news-card-content-wrap">
                      {item.imageUrl && (
                        <div className="news-card-image">
                          <img src={item.imageUrl} alt="" draggable={false} />
                        </div>
                      )}

                      <div className="news-card-content">
                        {item.bodyParagraphs.map((paragraph, index) => (
                          <p key={index} className="news-card-paragraph">
                            {paragraph}
                          </p>
                        ))}

                        <p className="news-card-reference">
                          <span className="news-card-reference-label">{item.reference.label}</span>
                          {item.reference.url ? (
                            <a
                              href={item.reference.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="news-card-reference-link"
                            >
                              {item.reference.text}
                            </a>
                          ) : (
                            <span className="news-card-reference-link">{item.reference.text}</span>
                          )}
                        </p>
                      </div>

                      {item.insight && (
                        <div className="news-card-insight">
                          <div className="news-card-insight-head">
                            <img src={IconInsight} alt="" draggable={false} className="news-card-insight-icon" />
                            <p className="news-card-insight-label">{item.insight.label}</p>
                          </div>
                          <p className="news-card-insight-content">{item.insight.content}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>

        {isDesktop && renderNavButton(1)}
      </div>
    </div>,
    modalRoot,
  );
};
